//----------------------------------------------------------------------------------------------------------------------
// Post-Processing Pipeline Backend
//
// Applies post-processing effects via BabylonJS DefaultRenderingPipeline and SSAO2RenderingPipeline.
//----------------------------------------------------------------------------------------------------------------------

import {
    Color4,
    DefaultRenderingPipeline,
    ImageProcessingConfiguration,
    SSAO2RenderingPipeline,
    type Scene,
} from '@babylonjs/core';

import type { PostProcessingConfig } from '../interfaces/level.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * Map from user-facing tonemap operator names to BabylonJS constants.
 * BabylonJS only has STANDARD (0) and ACES (1) — the other names all map to STANDARD.
 */
const TONEMAP_OPERATORS : Record<string, number> = {
    hable: ImageProcessingConfiguration.TONEMAPPING_STANDARD,
    reinhard: ImageProcessingConfiguration.TONEMAPPING_STANDARD,
    hejidawson: ImageProcessingConfiguration.TONEMAPPING_STANDARD,
    photographic: ImageProcessingConfiguration.TONEMAPPING_STANDARD,
    aces: ImageProcessingConfiguration.TONEMAPPING_ACES,
};

//----------------------------------------------------------------------------------------------------------------------

/**
 * Apply post-processing effects to a scene based on a declarative config.
 * Creates BabylonJS rendering pipelines and configures them according to the provided settings.
 *
 * This is a level-config handler, not a node property handler.
 */
export function applyPipelinePostProcessing(scene : Scene, config : PostProcessingConfig) : void
{
    // Can't set up post-processing without a camera
    if(!scene.activeCamera)
    {
        return;
    }

    // Warn about Frame Graph-only features
    if(config.volumetric)
    {
        console.warn('[SAGE] Volumetric lighting requires renderer: "frameGraph". Ignored in pipeline mode.');
    }

    // Create the default rendering pipeline
    const pipeline = new DefaultRenderingPipeline('sage-default', true, scene, scene.cameras);

    // Bloom
    if(config.bloom)
    {
        pipeline.bloomEnabled = true;

        if(config.bloom.weight !== undefined)
        {
            pipeline.bloomWeight = config.bloom.weight;
        }
        if(config.bloom.threshold !== undefined)
        {
            pipeline.bloomThreshold = config.bloom.threshold;
        }
        if(config.bloom.scale !== undefined)
        {
            pipeline.bloomScale = config.bloom.scale;
        }
        if(config.bloom.kernel !== undefined)
        {
            pipeline.bloomKernel = config.bloom.kernel;
        }
    }

    // Tone mapping
    if(config.tonemap)
    {
        pipeline.imageProcessingEnabled = true;
        pipeline.imageProcessing.toneMappingEnabled = true;

        if(config.tonemap.operator)
        {
            pipeline.imageProcessing.toneMappingType
                = TONEMAP_OPERATORS[config.tonemap.operator] ?? ImageProcessingConfiguration.TONEMAPPING_STANDARD;
        }
    }

    // Film grain
    if(config.grain)
    {
        pipeline.grainEnabled = true;

        if(config.grain.intensity !== undefined)
        {
            pipeline.grain.intensity = config.grain.intensity;
        }
        if(config.grain.animated !== undefined)
        {
            pipeline.grain.animated = config.grain.animated;
        }
    }

    // Vignette
    if(config.vignette)
    {
        pipeline.imageProcessingEnabled = true;
        pipeline.imageProcessing.vignetteEnabled = true;

        if(config.vignette.weight !== undefined)
        {
            pipeline.imageProcessing.vignetteWeight = config.vignette.weight;
        }
        if(config.vignette.stretch !== undefined)
        {
            pipeline.imageProcessing.vignetteStretch = config.vignette.stretch;
        }
        if(config.vignette.color)
        {
            pipeline.imageProcessing.vignetteColor = new Color4(
                config.vignette.color.r,
                config.vignette.color.g,
                config.vignette.color.b,
                1
            );
        }
    }

    // Sharpen
    if(config.sharpen)
    {
        pipeline.sharpenEnabled = true;

        if(config.sharpen.edge !== undefined)
        {
            pipeline.sharpen.edgeAmount = config.sharpen.edge;
        }
        if(config.sharpen.color !== undefined)
        {
            pipeline.sharpen.colorAmount = config.sharpen.color;
        }
    }

    // Chromatic aberration
    if(config.chromaticAberration)
    {
        pipeline.chromaticAberrationEnabled = true;

        if(config.chromaticAberration.amount !== undefined)
        {
            pipeline.chromaticAberration.aberrationAmount = config.chromaticAberration.amount;
        }
    }

    // SSAO — uses its own separate pipeline
    if(config.ssao)
    {
        const ssao = new SSAO2RenderingPipeline('sage-ssao', scene, { ssaoRatio: 0.5, blurRatio: 1.0 });

        if(config.ssao.radius !== undefined)
        {
            ssao.radius = config.ssao.radius;
        }
        if(config.ssao.samples !== undefined)
        {
            ssao.samples = config.ssao.samples;
        }
        if(config.ssao.totalStrength !== undefined)
        {
            ssao.totalStrength = config.ssao.totalStrength;
        }
    }
}

//----------------------------------------------------------------------------------------------------------------------
